// src/MobileFooterTabs.jsx
// Toggle portrait photos in color guide — set to true to show when ready
const SHOW_COLOR_GUIDE_PORTRAITS = true

import React from 'react'
import { INFO_COPY, linkifyCopy, MIC_SPECS } from './infoCopy'
import { colorOptions, colorDescriptions, screenColorOptions } from './colors'
import { colorGuidePortraits } from './colorGuidePortraits'
import ShareContent from './ShareContent'
import SavedDesignsTab from './SavedDesignsTab'
import { builderBus } from './builderBus'

/**
 * MobileFooterTabs
 * - Uses existing shareProduct() to generate Heroku-backed shareable links
 * - Official social media brand icons
 * - Ohma branding and fonts
 */
// In-memory store for last manually chosen tab -- resets on page refresh
let _lastManualTab = null

export default function MobileFooterTabs({
  initialTab = 'overview',
  initialColor = null,
  onInitialColorConsumed,
  config = {},
  onGenerateShareLink,
  shareScreenshotUrl,
  externalShareUrl,
  onRegenerateScreenshot,
  onApplyColors,
  onFooterToMiddle,
  onDismissOverlay,
  onShareTabActive,
  onMigratedId,
  currentDesignId,
  hasUnsavedChanges,
  currentDesignName,
  onLoadDesign,
  onSaveDesign,
  onUpdateDesign
}) {
  const storageKey = 'ohma:footer:lastTab'
  const [openAccordion, setOpenAccordion] = React.useState(null)
  const [showInstagramInstructions, setShowInstagramInstructions] = React.useState(false)
  const [linkCopied, setLinkCopied] = React.useState(false)
  const [shareUrl, setShareUrl] = React.useState('')
  const [isGeneratingLink, setIsGeneratingLink] = React.useState(false)
  const [canScrollMore, setCanScrollMore] = React.useState(false)
  const [expandedPhoto, setExpandedPhoto] = React.useState(null) // Track which photo is expanded
  const [expandedSpecialPhoto, setExpandedSpecialPhoto] = React.useState(null) // Track expanded special order photo
  const [expandedLogoPhoto, setExpandedLogoPhoto] = React.useState(null) // Track expanded logo photo
  const [mobileTabsScrolledLeft, setMobileTabsScrolledLeft] = React.useState(false)
  const [mobileTabsScrolledRight, setMobileTabsScrolledRight] = React.useState(false)
  const pmTabsRef = React.useRef(null)

  // Get active tab -- restore last manually chosen tab within same session,
  // but always start fresh on 'overview' for a new browser session.
  // If App.js passes a specific initialTab (share, colorGuide, etc.), use that.
  const [activeTab, setActiveTab] = React.useState(() => {
    // If App.js is routing to a specific tab, use that
    if (initialTab && initialTab !== 'overview') return initialTab
    // Restore last manually chosen tab within this page load (resets on refresh)
    return _lastManualTab || 'overview'
  })

  // Track whether the user has manually changed tabs in this page load
  const hasManuallyChangedTab = React.useRef(false)

  // Screenshot is triggered by App.js livePercent effect when footer reaches open position
  // Not triggered here to avoid mid-animation canvas flash/zoom

  // Listen for pad info button to open and scroll to specific accordion
  React.useEffect(() => {
    const handler = (e) => {
      setOpenAccordion(e.detail.id)
      setTimeout(() => {
        const el = document.getElementById(e.detail.id)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 150)
    }
    window.addEventListener('ohma:open-accordion', handler)
    return () => window.removeEventListener('ohma:open-accordion', handler)
  }, [])

  // Handle opening Instagram from instructions
  const handleOpenInstagram = async (e) => {
    // Prevent default to avoid any navigation
    if (e && e.preventDefault) {
      e.preventDefault()
    }


    // Use the App.js function
    let blob = null
    if (typeof builderBus.generateInstagramScreenshot === 'function') {
      try {
        blob = await builderBus.generateInstagramScreenshot()
      } catch (error) {
        console.error('Screenshot error:', error)
        alert('Failed to capture screenshot: ' + error.message)
        return
      }
    } else {
      alert('Screenshot function not ready. Please wait a moment.')
      return
    }

    if (!blob) {
      alert('Failed to capture screenshot. Please try again.')
      return
    }


    // Try Web Share API first (shows share sheet with Instagram options)
    if (navigator.share && navigator.canShare) {
      try {
        const file = new File([blob], 'my-ohma-microphone.png', { type: 'image/png' })

        if (navigator.canShare({ files: [file] })) {

          // Copy link first
          if (effectiveShareUrl) {
            copyToClipboard()
          }

          // Important: Don't await in a way that could cause navigation
          navigator
            .share({
              files: [file],
              text: buildShareText(effectiveShareUrl)
            })
            .then(() => {
            })
            .catch((error) => {
              if (error.name !== 'AbortError') {
              }
            })

          return
        }
      } catch (error) {
        // Fall through to download
      }
    }

    // Fallback: Download image
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'my-ohma-microphone.png'
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()

    setTimeout(() => {
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }, 100)

    if (effectiveShareUrl) {
      copyToClipboard()
    }
  }

  // restore last active tab from localStorage on first mount
  // Map 'beta' to 'feedback' and 'popfilter' to 'popFilter' for icon compatibility
  // Always default to 'overview' if nothing is saved

  // Reset expanded photo when tab changes
  React.useEffect(() => {
    setExpandedPhoto(null)
    setExpandedSpecialPhoto(null)
    // Reset My Builds detail view when navigating to/from the tab
    if (activeTab === 'savedDesigns') {
      builderBus.resetBuildsDetail?.()
    }
  }, [activeTab])

  const contentRef = React.useRef(null)

  // Sync with initialTab prop changes (when icon buttons update the prop)
  // Only fire when App.js is deliberately routing to a specific non-overview tab
  React.useEffect(() => {
    if (initialTab && initialTab !== 'overview' && initialTab !== activeTab) {
      switchToTab(initialTab)
    }
  }, [initialTab])

  // When footer closes and reopens, restore the last manually chosen tab
  // but only within the same page load (not across refreshes)
  React.useEffect(() => {
    if (!hasManuallyChangedTab.current) return
    // initialTab was reset to 'overview' by footer close -- restore user's last tab
    if (initialTab === 'overview' && activeTab !== 'overview') {
      // already on the right tab, nothing to do
    }
  }, [])

  // Center the active tab when it changes
  React.useEffect(() => {
    if (pmTabsRef.current) {
      const container = pmTabsRef.current
      const activeButton = container.querySelector(`[data-tab="${activeTab}"]`)
      if (activeButton) {
        const scrollLeft = activeButton.offsetLeft - container.offsetWidth / 2 + activeButton.offsetWidth / 2
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' })
      }
    }
  }, [activeTab])

  // Auto-open selected accordion when tab changes
  React.useEffect(() => {
    // Small delay to ensure accordion is rendered
    const timer = setTimeout(() => {
      if (activeTab === 'customLogo') {
        const logoValue = config.bottomScreenType === 'Custom Logo' ? 'custom' : 'ohma'
        setOpenAccordion(`logo-${logoValue}`)
      } else if (activeTab === 'micType') {
        const micType = config.microphoneType ? String(config.microphoneType).toLowerCase() : 'condenser'
        const micValue = micType === 'ribbon' ? 'whatIsRibbon' : 'whatIsCondenser'
        setOpenAccordion(`mic-${micValue}`)
      } else if (activeTab === 'screenType' && config.screenType) {
        setOpenAccordion(`screen-${String(config.screenType).toLowerCase()}`)
      } else if (activeTab === 'popFilter') {
        const popValue = !config.popFilterEnabled ? 'none' : config.popFilterMaterial === 'plastic' ? 'black' : 'custom'
        setOpenAccordion(`pop-${popValue}`)
      }
      // Note: colorGuide accordion opening is handled by the separate useEffect that depends on initialColor
    }, 100)

    return () => clearTimeout(timer)
  }, [activeTab])

  // Sync mic type accordion whenever config.microphoneType changes
  React.useEffect(() => {
    const micType = config.microphoneType ? String(config.microphoneType).toLowerCase() : 'condenser'
    setOpenAccordion(micType === 'ribbon' ? 'mic-whatIsRibbon' : 'mic-whatIsCondenser')
  }, [config.microphoneType])

  // Check initial scroll state for mobile tabs
  React.useEffect(() => {
    if (pmTabsRef.current) {
      const el = pmTabsRef.current
      setMobileTabsScrolledRight(el.scrollWidth > el.clientWidth)
    }
  }, [])

  // Generate share link when Share tab becomes active
  // Called directly on tab switch — not via useEffect — so it always fires fresh
  const switchToTab = React.useCallback(
    (key) => {
      if (key !== 'share') {
        setShowInstagramInstructions(false)
        if (onDismissOverlay) onDismissOverlay()
      } else {
        if (onShareTabActive) onShareTabActive() // tells App.js mobileFooterTab = 'share'
        if (onGenerateShareLink) onGenerateShareLink()
        if (onRegenerateScreenshot) onRegenerateScreenshot()
      }
      // Scroll content back to top when switching tabs, except colorGuide
      if (key !== 'colorGuide' && contentRef.current) {
        contentRef.current.scrollTop = 0
      }
      setActiveTab(key)
    },
    [onDismissOverlay, onGenerateShareLink, onRegenerateScreenshot, onShareTabActive]
  )

  // Auto-open accordions based on current selection
  React.useEffect(() => {
    if (activeTab === 'screenType' && config.screenType) {
      const currentScreenType = String(config.screenType).toLowerCase()
      setOpenAccordion(`screen-${currentScreenType}`)
    }
    if (activeTab === 'micType' && config.microphoneType) {
      const micType = String(config.microphoneType).toLowerCase()
      setOpenAccordion(micType === 'ribbon' ? 'mic-ribbon' : 'mic-condenser')
    }
    if (activeTab === 'popFilter') {
      const popOption = !config.popFilterEnabled ? 'pop-none' : config.popFilterMaterial === 'plastic' ? 'pop-black' : 'pop-custom'
      setOpenAccordion(popOption)
    }
    if (activeTab === 'customLogo') {
      // For now, default to standard logo
      setOpenAccordion('logo-standard')
    }
    // Open specific color accordion when Color Guide tab is opened with initialColor
    if (activeTab === 'colorGuide' && initialColor) {
      // Small delay to ensure the tab content has rendered
      setTimeout(() => {
        // Check colorOptions, screenColorOptions, and colorDescriptions
        const isStandardColor =
          colorOptions[initialColor] !== undefined || screenColorOptions[initialColor] !== undefined || colorDescriptions[initialColor] !== undefined
        // If it's a custom hex color (not in any color map), open 'custom'
        const accordionKey = isStandardColor ? `color-${initialColor}` : 'color-custom'
        // Always set it to ensure it opens
        setOpenAccordion(accordionKey)
        onInitialColorConsumed?.()
      }, 50)
    }
    // Open whichOption accordion when popFilter tab opened from info icon
    if (initialColor === 'whichOption') {
      setTimeout(() => {
        setOpenAccordion('pop-whichOption')
        onInitialColorConsumed?.()
      }, 50)
    }
  }, [activeTab, initialColor])

  // Auto-scroll to opened accordion in Color Guide
  React.useEffect(() => {
    if (activeTab === 'colorGuide' && openAccordion && openAccordion.startsWith('color-')) {
      setTimeout(() => {
        const accordion = document.querySelector(`[data-accordion-id="${openAccordion}"]`)
        if (accordion) {
          accordion.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 150)
    }
  }, [activeTab, openAccordion])


  // Use app-provided share URL if available, otherwise fall back to internally generated one
  const effectiveShareUrl = externalShareUrl || shareUrl

  const buildShareText = (url) => {
    const cfg = config || {}
    const lines = [
      `Check out my custom Ohma mic build:`,
      url || effectiveShareUrl || '',
      '',
      cfg.microphoneType ? `Microphone Type: ${cfg.microphoneType}` : null,
      cfg.color ? `Body Color: ${colorOptions[cfg.color] || cfg.color}` : null,
      cfg.screenType ? `Screen Type: ${cfg.screenType}` : null,
      cfg.screenColor ? `Screen Color: ${screenColorOptions[cfg.screenColor] || cfg.screenColor}` : null,
      cfg.fabricColor ? `Fabric Color: ${cfg.fabricColor}` : null,
      cfg.logoFill ? `Logo Fill: ${cfg.logoFill}` : null,
      cfg.logoType ? `Logo: ${cfg.logoType === 'custom' ? 'Custom Logo' : 'Ohma Logo'}` : null
    ]
      .filter((l) => l !== null)
      .join('\n')
    return lines
  }
  const shareText = buildShareText(effectiveShareUrl)

  const copyToClipboard = () => {
    if (effectiveShareUrl) {
      navigator.clipboard.writeText(effectiveShareUrl).then(() => {
        setLinkCopied(true)
        setTimeout(() => setLinkCopied(false), 2000)
      })
    }
  }

  const shareOptions = [
    {
      name: 'Facebook',
      icon: (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
      color: '#1877f2',
      action: () => {
        if (effectiveShareUrl) {
          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(effectiveShareUrl)}`, '_blank')
        }
      }
    },
    {
      name: 'Story',
      icon: (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
      ),
      color: '#E4405F',
      action: () => {
        setShowInstagramInstructions(true)
      }
    },
    {
      name: 'WhatsApp',
      icon: (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      ),
      color: '#25d366',
      action: () => {
        if (effectiveShareUrl) {
          window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + effectiveShareUrl)}`, '_blank')
        }
      }
    },
    {
      name: 'Messenger',
      icon: (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
          <path d="M12 0C5.373 0 0 4.975 0 11.111c0 3.497 1.745 6.616 4.472 8.652V24l4.086-2.242c1.09.301 2.246.464 3.442.464 6.627 0 12-4.974 12-11.111C24 4.975 18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z" />
        </svg>
      ),
      color: '#0084ff',
      action: () => {
        if (effectiveShareUrl) {
          window.open(
            `https://www.facebook.com/dialog/send?link=${encodeURIComponent(effectiveShareUrl)}&app_id=YOUR_APP_ID&redirect_uri=${encodeURIComponent(
              effectiveShareUrl
            )}`,
            '_blank'
          )
        }
      }
    },
    {
      name: 'Instagram',
      icon: (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
      ),
      color: '#E4405F',
      action: () => {
        if (effectiveShareUrl) {
          copyToClipboard()
        }
      }
    },
    {
      name: 'Email',
      icon: (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
        </svg>
      ),
      color: '#EA4335',
      action: () => {
        if (effectiveShareUrl) {
          window.location.href = `mailto:?subject=${encodeURIComponent('My Ohma Mic Build')}&body=${encodeURIComponent(buildShareText(effectiveShareUrl))}`
        }
      }
    },
    {
      name: 'Copy',
      icon: (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
          <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
        </svg>
      ),
      color: '#666',
      action: copyToClipboard
    },
    {
      name: 'SMS',
      icon: (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
          <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM9 11H7V9h2v2zm4 0h-2V9h2v2zm4 0h-2V9h2v2z" />
        </svg>
      ),
      color: '#34c759',
      action: () => {
        if (effectiveShareUrl) {
          window.location.href = `sms:?body=${encodeURIComponent(buildShareText(effectiveShareUrl))}`
        }
      }
    }
  ]


  return (
    <div className="footer-tab-panel" role="region" aria-label="Product info" style={{ background: '#fff', position: 'relative', minHeight: '100%' }}>
      {/* Left gradient - only when scrolled */}
      {mobileTabsScrolledLeft && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '20px',
            background: 'linear-gradient(to right, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%)',
            pointerEvents: 'none',
            zIndex: 2
          }}
        />
      )}
      {/* Right gradient - only when can scroll more */}
      {mobileTabsScrolledRight && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '20px',
            background: 'linear-gradient(to left, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%)',
            pointerEvents: 'none',
            zIndex: 2
          }}
        />
      )}

      <div
        className="pm-tabs"
        ref={pmTabsRef}
        role="tablist"
        aria-label="Footer product pages"
        onScroll={(e) => {
          const el = e.target
          setMobileTabsScrolledLeft(el.scrollLeft > 10)
          setMobileTabsScrolledRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10)
        }}
        style={{
          display: 'flex',
          gap: 10,
          padding: '12px 16px',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
        <style>{`
          .pm-tabs::-webkit-scrollbar { display: none; }
          .pm-page { 
            transition: opacity 200ms ease, transform 200ms ease;
          }
          .pm-page h3 {
            font-size: 20px;
            font-weight: 700;
            font-family: 'roc-grotesk-wide', sans-serif;
            margin-top: 0;
            margin-bottom: 12px;
          }
        `}</style>
        {[
          ['share', 'Share'],
          ['savedDesigns', 'My Builds'],
          ['overview', 'Overview'],
          ['micType', 'Mic Type'],
          ['screenType', 'Screen Type'],
          ['specs', 'Specs'],
          ['colorGuide', 'Color Guide'],
          ['customLogo', 'Custom Logo'],
          ['popFilter', 'Pop Filter'],
          ['specialOrders', 'Special Orders'],
          ['feedback', 'Give Feedback']
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            data-tab={key}
            className={`pm-tab ${activeTab === key ? 'pm-active' : ''}`}
            role="tab"
            aria-selected={activeTab === key}
            onClick={() => {
              switchToTab(key)
              try {
                if (typeof window !== 'undefined') {
                  window.localStorage.setItem(storageKey, key)
                  _lastManualTab = key
                }
              } catch (err) {}
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ position: 'relative', top: '1px' }}>{label}</span>
            {key === 'share' && (
              <svg
                width="11"
                height="11"
                viewBox="3 0 18 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ position: 'relative', top: '0px' }}>
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <polyline points="16 6 12 2 8 6" />
                <line x1="12" y1="2" x2="12" y2="15" />
              </svg>
            )}
          </button>
        ))}
      </div>

      <div
        className="footer-tab-content"
        style={{ padding: '0 16px', paddingBottom: '200px', maxHeight: 'calc(100vh - 160px)', overflowY: 'auto', position: 'relative', overflowX: 'hidden' }}
        ref={contentRef}
        onScroll={(e) => {
          const element = e.target
          const canScroll = element.scrollTop < element.scrollHeight - element.clientHeight - 20
          setCanScrollMore(canScroll)
        }}>
        {/* Top gradient */}
        <div
          style={{
            position: 'sticky',
            top: -12,
            left: -16,
            right: -16,
            height: '20px',
            background: 'linear-gradient(to bottom, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%)',
            pointerEvents: 'none',
            zIndex: 10,
            marginLeft: '-16px',
            marginRight: '-16px',
            marginTop: '-12px',
            marginBottom: '0px'
          }}
        />

        {/* Overview */}
        <div
          className="pm-page"
          style={{ display: activeTab === 'overview' ? 'block' : 'none', fontFamily: 'Jost, sans-serif' }}
          aria-hidden={activeTab !== 'overview'}>
          <h3 style={{ marginTop: 0, fontFamily: "'roc-grotesk-wide', sans-serif", fontWeight: 600 }}>About the Build</h3>

          <div style={{ marginTop: 16 }}>
            {(() => {
              const overviewTopics = INFO_COPY.overview

              return overviewTopics.map((topic) => {
                const isOpen = openAccordion === `overview-${topic.id}`

                return (
                  <div
                    key={topic.id}
                    style={{
                      marginBottom: 12,
                      border: '1px solid #e6e8ee',
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: '#fff'
                    }}>
                    <div
                      onClick={() => setOpenAccordion(isOpen ? null : `overview-${topic.id}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: 12,
                        cursor: 'pointer'
                      }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, fontFamily: 'Jost, sans-serif' }}>{topic.title}</div>
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#999', fontFamily: 'Jost, sans-serif' }}>{isOpen ? '−' : '+'}</div>
                    </div>
                    {isOpen && (
                      <div style={{ padding: '0 12px 12px 12px', borderTop: '1px solid #e6e8ee' }}>
                        <div style={{ marginTop: 12 }}>
                          {topic.paragraphs.map((p, i) => (
                            <p key={i} style={{ fontFamily: 'Jost, sans-serif', marginTop: 0, marginBottom: 8 }}>
                              {linkifyCopy(p)}
                            </p>
                          ))}
                          {topic.items && (
                            <ul style={{ marginTop: 0, marginBottom: 8, fontFamily: 'Jost, sans-serif' }}>
                              {topic.items.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          )}
                          {topic.notes &&
                            topic.notes.map((note, i) => (
                              <p key={i} style={{ fontFamily: 'Jost, sans-serif', marginTop: 0, marginBottom: 8 }}>
                                {note}
                              </p>
                            ))}
                          {topic.specialOrdersLink && (
                            <p style={{ fontFamily: 'Jost, sans-serif', marginTop: 0, marginBottom: 0 }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenAccordion(null)
                                  setActiveTab('specialOrders')
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  color: 'inherit',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  fontFamily: 'Jost, sans-serif',
                                  fontSize: 'inherit'
                                }}>
                                Special orders
                              </button>{' '}
                              placed outside of this build process may take four to six weeks or longer depending on the request.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })
            })()}
          </div>
        </div>

        {/* Gallery */}
        <div className="pm-page" style={{ display: activeTab === 'gallery' ? 'block' : 'none' }} aria-hidden={activeTab !== 'gallery'}>
          <h3 style={{ marginTop: 0, fontFamily: "'roc-grotesk-wide', sans-serif", fontWeight: 600 }}>Gallery</h3>
          <p style={{ color: '#666', fontFamily: 'Jost, sans-serif' }}>Photo gallery coming soon...</p>
        </div>

        {/* Screen Type */}
        <div className="pm-page" style={{ display: activeTab === 'screenType' ? 'block' : 'none' }} aria-hidden={activeTab !== 'screenType'}>
          <h3 style={{ marginTop: 0, fontFamily: "'roc-grotesk-wide', sans-serif", fontWeight: 600 }}>Screen Type</h3>
          <div style={{ marginTop: 16 }}>
            <div>
              {(() => {
                const screenTopics = INFO_COPY.screenType.map((t) => ({ ...t, label: t.title }))

                const currentScreenType = config.screenType ? String(config.screenType).toLowerCase() : 'motif'
                const screenOrder = ['motif', 'stripes', 'windows', 'scales', 'holes']
                const screenQaIds = ['whatIsScreen', 'howScreensAffect', 'whyNotMesh', 'whenToUse', 'doesFabricType']
                const sortedScreenTopics = [...screenTopics].sort((a, b) => {
                  const aIsQa = screenQaIds.includes(a.id)
                  const bIsQa = screenQaIds.includes(b.id)
                  if (aIsQa && bIsQa) return screenQaIds.indexOf(a.id) - screenQaIds.indexOf(b.id)
                  if (aIsQa) return -1
                  if (bIsQa) return 1
                  if (a.id === currentScreenType) return -1
                  if (b.id === currentScreenType) return 1
                  return screenOrder.indexOf(a.id) - screenOrder.indexOf(b.id)
                })

                return sortedScreenTopics.map((topic) => {
                  const isScreenOption = ['motif', 'scales', 'windows', 'stripes', 'holes'].includes(topic.id)
                  const isSelected = isScreenOption && topic.id === currentScreenType
                  const isOpen = openAccordion === `screen-${topic.id}`

                  return (
                    <div
                      key={topic.id}
                      style={{
                        marginBottom: 12,
                        border: isSelected ? '2px solid var(--accent, #6186F7)' : '1px solid #e6e8ee',
                        borderRadius: 8,
                        overflow: 'hidden',
                        background: isSelected ? '#f6f8ff' : '#fff'
                      }}>
                      <div
                        onClick={() => setOpenAccordion(isOpen ? null : `screen-${topic.id}`)}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, cursor: 'pointer' }}>
                        {topic.image && (
                          <div
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: 6,
                              background: '#f0f1f3',
                              flexShrink: 0,
                              overflow: 'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                            <img
                              src={topic.image}
                              alt={topic.label}
                              style={{
                                width: topic.image.endsWith('.svg') ? '75%' : '100%',
                                height: topic.image.endsWith('.svg') ? '75%' : '100%',
                                objectFit: 'contain'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none'
                              }}
                            />
                          </div>
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ fontWeight: 700, fontSize: 15, fontFamily: 'Jost, sans-serif' }}>{topic.label}</div>
                            {isSelected && (
                              <span
                                style={{
                                  fontSize: 11,
                                  background: 'var(--accent, #6186F7)',
                                  color: '#fff',
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                  fontWeight: 600
                                }}>
                                Selected
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#999', fontFamily: 'Jost, sans-serif' }}>{isOpen ? '−' : '+'}</div>
                      </div>
                      {isOpen && (
                        <div style={{ padding: '0 12px 12px 12px', borderTop: '1px solid #e6e8ee' }}>
                          {topic.paragraphs.map((p, i) => (
                            <p key={i} style={{ color: '#666', marginTop: i === 0 ? 12 : 8, marginBottom: 0, fontFamily: 'Jost, sans-serif' }}>
                              {p}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })
              })()}
            </div>
          </div>
        </div>

        {/* Custom Logo */}
        <div className="pm-page" style={{ display: activeTab === 'customLogo' ? 'block' : 'none' }} aria-hidden={activeTab !== 'customLogo'}>
          <h3 style={{ marginTop: 0, fontFamily: "'roc-grotesk-wide', sans-serif", fontWeight: 600 }}>Custom Logo</h3>
          <div style={{ marginTop: 16 }}>
            {(() => {
              const logoTopics = INFO_COPY.customLogo.map((t) => ({ ...t, label: t.title }))

              return logoTopics.map((topic) => {
                const isOpen = openAccordion === `logo-${topic.id}`
                const renderWithEmail = (text) => linkifyCopy(text)
                return (
                  <div key={topic.id} style={{ marginBottom: 12, border: '1px solid #e6e8ee', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                    <div
                      onClick={() => setOpenAccordion(isOpen ? null : `logo-${topic.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, cursor: 'pointer' }}>
                      <div style={{ flex: 1, fontWeight: 700, fontSize: 15, fontFamily: 'Jost, sans-serif' }}>{topic.label}</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#999', fontFamily: 'Jost, sans-serif' }}>{isOpen ? '−' : '+'}</div>
                    </div>
                    {isOpen && (
                      <div style={{ padding: '0 12px 12px 12px', borderTop: '1px solid #e6e8ee' }}>
                        {topic.paragraphs.map((p, i) => (
                          <p key={i} style={{ color: '#666', marginTop: i === 0 ? 12 : 8, marginBottom: 0, fontFamily: 'Jost, sans-serif' }}>
                            {renderWithEmail(p)}
                          </p>
                        ))}
                        {topic.id === 'whatIsLogo' &&
                          (() => {
                            const logoPhotos = [
                              {
                                src: 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/ohma-custom-logo-3.webp',
                                name: 'Custom Logo for Stella Studios',
                                description: 'Stella Studios is a recording studio in Nashville, TN. They wanted to represent their studio on their mic.'
                              },
                              {
                                src: 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/ohma-custom-logo-2.webp',
                                name: 'Custom Logo for SHE-Q',
                                description:
                                  'SHE-Q is a Canadian non-profit providing a platform for women and non-binary producers and engineers. They believe these voices are vital to the future of music, and so do we. Getting to work with them and put their logo on a mic means a lot.'
                              },
                              {
                                src: 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/ohma-custom-logo-4.webp',
                                name: 'Custom Logo for ACLU SoCal',
                                description:
                                  'Three days before the LA Pride Parade, ACLU SoCal reached out and asked us to build a microphone that embodied the trans pride flag for Janelle Monae to perform with. As a queer-owned company, we said yes immediately. We designed, built, and finished it in time. Seeing it on stage with Janelle Monae at the parade was one of those moments we will never forget.'
                              }
                            ]
                            return (
                              <>
                                {expandedLogoPhoto === null && (
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginTop: '16px' }}>
                                    {logoPhotos.map((photo, i) => (
                                      <div
                                        key={i}
                                        onClick={() => setExpandedLogoPhoto(i)}
                                        style={{
                                          aspectRatio: '3/4',
                                          backgroundColor: '#f5f5f5',
                                          borderRadius: '8px',
                                          border: '1px solid #e0e0e0',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: '#999',
                                          fontSize: '12px',
                                          fontFamily: 'Jost, sans-serif',
                                          cursor: 'pointer',
                                          transition: 'transform 0.2s',
                                          backgroundImage: photo.src ? `url("${photo.src}")` : 'none',
                                          backgroundSize: 'cover',
                                          backgroundPosition: 'center'
                                        }}
                                        onTouchStart={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                                        onTouchEnd={(e) => (e.currentTarget.style.transform = 'scale(1)')}>
                                        {!photo.src && <span>{i + 1}</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {expandedLogoPhoto !== null &&
                                  (() => {
                                    const expandedItem = logoPhotos[expandedLogoPhoto]
                                    return (
                                      <div
                                        style={{
                                          position: 'fixed',
                                          top: 0,
                                          left: 0,
                                          right: 0,
                                          bottom: 0,
                                          backgroundColor: '#fff',
                                          borderTopLeftRadius: '24px',
                                          borderTopRightRadius: '24px',
                                          zIndex: 1000,
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          justifyContent: 'flex-start',
                                          paddingTop: '80px',
                                          paddingLeft: '20px',
                                          paddingRight: '20px',
                                          paddingBottom: '40px',
                                          overflowY: 'auto'
                                        }}>
                                        <div
                                          style={{
                                            position: 'absolute',
                                            top: '12px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: '40px',
                                            height: '4px',
                                            backgroundColor: '#d1d5db',
                                            borderRadius: '2px'
                                          }}
                                        />
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            setExpandedLogoPhoto(null)
                                          }}
                                          style={{
                                            position: 'absolute',
                                            top: '20px',
                                            left: '20px',
                                            background: 'transparent',
                                            border: 'none',
                                            padding: '8px 0',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            color: '#666',
                                            fontSize: 14,
                                            fontWeight: 600,
                                            fontFamily: 'Jost, sans-serif'
                                          }}>
                                          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                                          </svg>
                                          Back to Custom Logo
                                        </button>
                                        <div style={{ width: '70%', maxWidth: '360px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                          <div
                                            style={{
                                              width: '100%',
                                              width: '100%',
                                              aspectRatio: '3/4',
                                              maxHeight: '55vh',
                                              backgroundColor: '#f5f5f5',
                                              borderRadius: '12px',
                                              border: '2px solid #e0e0e0',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              color: '#999',
                                              fontSize: '24px',
                                              fontFamily: 'Jost, sans-serif',
                                              backgroundImage: expandedItem.src ? `url("${expandedItem.src}")` : 'none',
                                              backgroundSize: 'cover',
                                              backgroundPosition: 'center'
                                            }}>
                                            {!expandedItem.src && <span>{expandedLogoPhoto + 1}</span>}
                                          </div>
                                          <div
                                            style={{
                                              fontFamily: "'roc-grotesk-wide', sans-serif",
                                              fontWeight: 700,
                                              fontSize: 15,
                                              color: '#222',
                                              textAlign: 'center'
                                            }}>
                                            {expandedItem.name}
                                          </div>
                                          {expandedItem.description && (
                                            <p
                                              style={{
                                                color: '#666',
                                                fontSize: 13,
                                                lineHeight: 1.6,
                                                textAlign: 'left',
                                                margin: '8px 0 0',
                                                fontFamily: 'Jost, sans-serif'
                                              }}>
                                              {expandedItem.description}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    )
                                  })()}
                              </>
                            )
                          })()}
                      </div>
                    )}
                  </div>
                )
              })
            })()}
          </div>
        </div>

        {/* Mic Type */}
        <div className="pm-page" style={{ display: activeTab === 'micType' ? 'block' : 'none' }} aria-hidden={activeTab !== 'micType'}>
          <h3 style={{ marginTop: 0, fontFamily: "'roc-grotesk-wide', sans-serif", fontWeight: 600 }}>Mic Type</h3>
          <div style={{ marginTop: 16 }}>
            {(() => {
              const micTopics = INFO_COPY.micType.map((t) => ({ ...t, label: t.title }))

              const currentMicType = config.microphoneType ? String(config.microphoneType).toLowerCase() : 'condenser'
              const primaryMicId = currentMicType === 'ribbon' ? 'whatIsRibbon' : 'whatIsCondenser'
              const sortedMicTopics = [...micTopics].sort((a, b) => {
                if (a.id === primaryMicId) return -1
                if (b.id === primaryMicId) return 1
                return 0
              })

              return sortedMicTopics.map((topic) => {
                const isOpen = openAccordion === `mic-${topic.id}`
                return (
                  <div
                    key={topic.id}
                    id={`mic-${topic.id}`}
                    style={{ marginBottom: 12, border: '1px solid #e6e8ee', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                    <div
                      onClick={() => setOpenAccordion(isOpen ? null : `mic-${topic.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, cursor: 'pointer' }}>
                      <div style={{ flex: 1, fontWeight: 700, fontSize: 15, fontFamily: 'Jost, sans-serif' }}>{topic.label}</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#999', fontFamily: 'Jost, sans-serif' }}>{isOpen ? '−' : '+'}</div>
                    </div>
                    {isOpen && (
                      <div style={{ padding: '0 12px 12px 12px', borderTop: '1px solid #e6e8ee' }}>
                        {topic.paragraphs.map((p, i) => (
                          <p key={i} style={{ color: '#666', marginTop: i === 0 ? 12 : 8, marginBottom: 0, fontFamily: 'Jost, sans-serif' }}>
                            {p}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })
            })()}
          </div>
        </div>

        {/* Specs Tab */}
        <div className="pm-page" style={{ display: activeTab === 'specs' ? 'block' : 'none' }} aria-hidden={activeTab !== 'specs'}>
          <h3 style={{ marginTop: 0, fontFamily: "'roc-grotesk-wide', sans-serif", fontWeight: 600 }}>Specs</h3>
          <div style={{ marginTop: 16 }}>
            {(() => {
              const specsTopics = [
                { id: 'condenser', label: 'Condenser' },
                { id: 'ribbon', label: 'Ribbon' },
                { id: 'screens', label: 'Screens' }
              ]

              const specsRow = (label, value) => (
                <div key={label} style={{ display: 'flex', gap: 12, paddingTop: 10, paddingBottom: 10, borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ width: '40%', fontSize: 13, fontWeight: 600, color: '#444', flexShrink: 0, fontFamily: 'Jost, sans-serif' }}>{label}</div>
                  <div style={{ fontSize: 13, color: '#666', lineHeight: 1.5, fontFamily: 'Jost, sans-serif' }}>{value}</div>
                </div>
              )

              return specsTopics.map((topic) => {
                const isOpen = openAccordion === `specs-${topic.id}`
                return (
                  <div key={topic.id} style={{ marginBottom: 12, border: '1px solid #e6e8ee', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                    <div
                      onClick={() => setOpenAccordion(isOpen ? null : `specs-${topic.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, cursor: 'pointer' }}>
                      <div style={{ flex: 1, fontWeight: 600, fontSize: 15, fontFamily: "'roc-grotesk-wide', sans-serif" }}>{topic.label}</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#999', fontFamily: 'Jost, sans-serif' }}>{isOpen ? '−' : '+'}</div>
                    </div>
                    {isOpen && (
                      <div style={{ padding: '0 12px 16px 12px', borderTop: '1px solid #e6e8ee' }}>
                        {topic.id === 'condenser' && (
                          <>
                            <p style={{ color: '#666', marginTop: 12, marginBottom: 16, fontFamily: 'Jost, sans-serif', lineHeight: 1.6, fontSize: 14 }}>
                              Our condenser is built around The Debby, our own edge-terminated, single-backplate capsule design, tuned in-house in East Los
                              Angeles. It is not a clone of a vintage capsule or a copy of an existing circuit. Its voicing was developed over years of
                              iteration to deliver a modern condenser sound with real low-end weight, an intact midrange, and a lifted transient top end that
                              stays controlled instead of turning brittle. The result is a cardioid microphone that feels forward, dimensional, and composed at
                              the source.
                            </p>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                color: '#999',
                                marginBottom: 4,
                                fontFamily: "'roc-grotesk-wide', sans-serif"
                              }}>
                              Core Specifications
                            </div>
                            {MIC_SPECS.condenser.map(([label, value]) => specsRow(label, value))}
                            <p style={{ color: '#666', fontSize: 13, marginTop: 16, marginBottom: 8, fontFamily: 'Jost, sans-serif', lineHeight: 1.5 }}>
                              <strong>Hear the Condenser</strong>
                              <br />
                              Explore the Listening Library for unprocessed examples across vocals, instruments, and screen sets.
                            </p>
                            <a
                              href="https://ohmaworld.com/pages/manual"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#666', fontSize: 13, fontFamily: 'Jost, sans-serif', textDecoration: 'underline' }}>
                              See the full manual
                            </a>
                          </>
                        )}
                        {topic.id === 'ribbon' && (
                          <>
                            <p style={{ color: '#666', marginTop: 12, marginBottom: 16, fontFamily: 'Jost, sans-serif', lineHeight: 1.6, fontSize: 14 }}>
                              Our ribbon uses a 1.8 µm aluminum element suspended in a custom magnetic motor, tuned and built in-house in East Los Angeles.
                              Each ribbon is tensioned and aligned by hand, using processes developed through years of working with vintage and modern ribbon
                              microphones. It produces a true figure-8 response with strong low-end weight, natural proximity behavior, and a smooth, grounded
                              top end. The result is a more dimensional, immersive sound that captures both the source and the space around it.
                            </p>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                color: '#999',
                                marginBottom: 4,
                                fontFamily: "'roc-grotesk-wide', sans-serif"
                              }}>
                              Core Specifications
                            </div>
                            {MIC_SPECS.ribbon.map(([label, value]) => specsRow(label, value))}
                            <p style={{ color: '#666', fontSize: 13, marginTop: 16, marginBottom: 8, fontFamily: 'Jost, sans-serif', lineHeight: 1.5 }}>
                              <strong>Hear the Ribbon</strong>
                              <br />
                              Visit the Listening Library for unprocessed examples across vocals, amps, room capture, and screen sets.
                            </p>
                            <a
                              href="https://ohmaworld.com/pages/manual"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#666', fontSize: 13, fontFamily: 'Jost, sans-serif', textDecoration: 'underline' }}>
                              See the full manual
                            </a>
                          </>
                        )}
                        {topic.id === 'screens' && (
                          <>
                            <p style={{ color: '#666', marginTop: 12, marginBottom: 12, fontFamily: 'Jost, sans-serif', lineHeight: 1.6, fontSize: 14 }}>
                              On an Ohma, the screens are engineered to affect both sound and appearance. Each pattern produces a distinct frequency response by
                              changing how air moves before it reaches the ribbon or capsule. More open designs can feel broader and more natural, while more
                              structured patterns can shape top-end response, proximity effect, plosive control, low-end behavior, and midrange focus.
                            </p>
                            <p style={{ color: '#666', marginTop: 0, marginBottom: 12, fontFamily: 'Jost, sans-serif', lineHeight: 1.6, fontSize: 14 }}>
                              The screens attach magnetically and can be removed with a guitar pick or the edge of a slim card. Unlike most microphones, which
                              are fixed to a single grille design, Ohma allows the acoustic structure of the mic to change without replacing the microphone
                              itself.
                            </p>
                            <p style={{ color: '#666', marginTop: 0, marginBottom: 0, fontFamily: 'Jost, sans-serif', lineHeight: 1.6, fontSize: 14 }}>
                              For a deeper explanation of how each pattern behaves on each mic type, visit the{' '}
                              <button
                                type="button"
                                onClick={() => switchToTab('screenType')}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  color: '#666',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  fontSize: 'inherit',
                                  fontFamily: 'inherit'
                                }}>
                                Screen Type
                              </button>{' '}
                              tab.
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            })()}
          </div>
        </div>

        {/* Color Guide Tab */}
        <div className="pm-page" style={{ display: activeTab === 'colorGuide' ? 'block' : 'none' }} aria-hidden={activeTab !== 'colorGuide'}>
          <h3 style={{ marginTop: 0, fontFamily: "'roc-grotesk-wide', sans-serif", fontWeight: 600 }}>Color Guide</h3>
          <div style={{ marginTop: 16 }}>
            {(() => {
              // Map of colors that have texture/image swatches
              const colorEntries = [
                ['__cerakote__', "What's special about Ohma paint?"],
                ['__finishes__', 'What type of finishes do you offer?'],
                ...Object.entries(colorOptions).reduce((acc, entry) => {
                  acc.push(entry)
                  if (entry[1] === 'Brass') acc.push(['Stainless Steel', 'Stainless Steel'])
                  return acc
                }, []),
                ['custom', 'Custom Colors'] // Add custom colors option
              ]

              return colorEntries.map(([value, name]) => {
                const isFinishesInfo = value === '__finishes__'
                const isCerakoteInfo = value === '__cerakote__'
                const isCustomColor = value === 'custom'
                const colorInfo = isCustomColor
                  ? {
                      name: 'Custom Colors',
                      rectangleSwatchUrl: 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260330_Swatches_Custom-Hex.webp',
                      description:
                        "There are 16,777,216 possible hexadecimal colors. We're willing to mix yours. Choose from the HEX picker and we'll develop a custom color recipe, blended and refined specifically for your build. Keep in mind, digital previews may not perfectly match the final physical finish.",
                      isPremium: true,
                      premiumPrice: 199
                    }
                  : isFinishesInfo
                  ? { name: 'What type of finishes do you offer?' }
                  : isCerakoteInfo
                  ? { name: "What's special about Ohma paint?" }
                  : colorDescriptions[value] || {}
                const accordionKey = `color-${value}`
                const isOpen = openAccordion === accordionKey

                // Finishes info entry — simple accordion, no swatch or selected state
                if (isCerakoteInfo) {
                  return (
                    <div key={value} style={{ marginBottom: 12, border: '1px solid #e6e8ee', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                      <div
                        onClick={() => setOpenAccordion(isOpen ? null : accordionKey)}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, cursor: 'pointer' }}>
                        <div style={{ flex: 1, fontWeight: 700, fontSize: 15, fontFamily: 'Jost, sans-serif' }}>{name}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#999', fontFamily: 'Jost, sans-serif' }}>{isOpen ? '−' : '+'}</div>
                      </div>
                      {isOpen && (
                        <div style={{ padding: '0 12px 12px 12px', borderTop: '1px solid #e6e8ee' }}>
                          <p style={{ color: '#666', marginTop: 12, marginBottom: 8, fontFamily: 'Jost, sans-serif' }}>
                            Ohma microphones are finished in Cerakote, a specialized ceramic-based coating used in aerospace and other high-performance
                            applications. While it is often thought of as paint, it behaves very differently, holding tighter tolerances, delivering stronger
                            consistency, and producing a cleaner surface than the finishes typically used on production microphones. It is a major reason Ohma
                            mics are known for their sharp, exact, and unmistakably clean look.
                          </p>
                          <p style={{ color: '#666', marginTop: 0, marginBottom: 8, fontFamily: 'Jost, sans-serif' }}>
                            All painting happens in-house in our East Los Angeles workshop. Every part is sprayed and oven-cured for about an hour and a half,
                            with temperature and timing tightly controlled because even small variations can shift how a color develops.
                          </p>
                          <p style={{ color: '#666', marginTop: 0, marginBottom: 8, fontFamily: 'Jost, sans-serif' }}>
                            Most Cerakote work is done through third-party shops in large batches. Painting a single unit that way can easily cost hundreds of
                            dollars, which forces companies to either limit options or pass that cost directly to the customer. We chose not to build around
                            that model. We built our own paint process so we could stay flexible, work one microphone at a time, and keep that level of finish
                            accessible without inflating the price.
                          </p>
                          <p style={{ color: '#666', marginTop: 0, marginBottom: 8, fontFamily: 'Jost, sans-serif' }}>
                            We develop and mix our own colors in-house. That includes matching specific tones, including hex-based selections from the
                            customizer, by sourcing and dialing in coatings through careful formulation.
                          </p>
                          <p style={{ color: '#666', marginTop: 0, marginBottom: 0, fontFamily: 'Jost, sans-serif' }}>
                            This is why Ohma finishes look the way they do. Our approach takes more time, but it allows us to stay flexible and treat each
                            custom microphone as its own build rather than part of a fixed run.
                          </p>
                        </div>
                      )}
                    </div>
                  )
                }

                if (isFinishesInfo) {
                  return (
                    <div key={value} style={{ marginBottom: 12, border: '1px solid #e6e8ee', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                      <div
                        onClick={() => setOpenAccordion(isOpen ? null : accordionKey)}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, cursor: 'pointer' }}>
                        <div style={{ flex: 1, fontWeight: 700, fontSize: 15, fontFamily: 'Jost, sans-serif' }}>{name}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#999', fontFamily: 'Jost, sans-serif' }}>{isOpen ? '−' : '+'}</div>
                      </div>
                      {isOpen && (
                        <div style={{ padding: '0 12px 12px 12px', borderTop: '1px solid #e6e8ee' }}>
                          <p style={{ color: '#666', marginTop: 12, marginBottom: 8, fontFamily: 'Jost, sans-serif' }}>
                            For color finishes, we use a ceramic-based coating that forms a sleek, durable film over the metal. Depending on the specific color
                            formulation, finishes may range from textured to smooth, matte to gloss, and in some cases include a subtle shimmer. These surface
                            qualities are inherent to the individual color itself.
                          </p>
                          <p style={{ color: '#666', marginTop: 0, marginBottom: 8, fontFamily: 'Jost, sans-serif' }}>
                            We offer solid brass bodies, hand-polished and sealed with a clear coat to preserve their natural tone without patina.
                          </p>
                          <p style={{ color: '#666', marginTop: 0, marginBottom: 0, fontFamily: 'Jost, sans-serif' }}>
                            Our raw aluminum bodies are sandblasted in-house and clear coated, creating a clean, industrial silver surface.
                          </p>
                        </div>
                      )}
                    </div>
                  )
                }

                // Check if this color (including custom) is currently selected
                // For custom colors: check if config has a hex that's NOT in our colorOptions list
                const bodyIsCustom = config.color && config.color.startsWith('#') && !colorOptions[config.color]
                const screenIsCustom = config.screenColor && config.screenColor.startsWith('#') && !colorOptions[config.screenColor]
                const isColorSelected = isCustomColor ? bodyIsCustom || screenIsCustom : value === config.color || value === config.screenColor

                // Determine swatch style - use circleSwatchUrl if available, otherwise hex color, or gradient for custom
                const swatchStyle = isCustomColor
                  ? { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)', border: '2px solid #e0e0e0' }
                  : colorInfo.circleSwatchUrl
                  ? { backgroundImage: `url("${colorInfo.circleSwatchUrl}")`, backgroundSize: 'cover', backgroundPosition: 'center' }
                  : { backgroundColor: value }

                return (
                  <div
                    key={value}
                    data-accordion-id={accordionKey}
                    style={{
                      marginBottom: 12,
                      border: '1px solid #e6e8ee',
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: '#fff',
                      outline: isColorSelected ? '2px solid var(--accent, #6186F7)' : 'none',
                      outlineOffset: '-1px'
                    }}>
                    {/* Header */}
                    <div
                      onClick={() => setOpenAccordion(isOpen ? null : accordionKey)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: 12,
                        cursor: 'pointer'
                      }}>
                      {/* Color Swatch */}
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          border: '2px solid #e0e0e0',
                          flexShrink: 0,
                          ...swatchStyle
                        }}
                      />

                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 15, fontFamily: "'roc-grotesk-wide', sans-serif" }}>{colorInfo.name || name}</div>
                      </div>
                      {(() => {
                        const showBadge = isColorSelected
                        return (
                          showBadge && (
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: 'var(--accent, #6186F7)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: 2
                              }}>
                              {(isCustomColor ? bodyIsCustom : value === config.color) && <div>Body</div>}
                              {(isCustomColor ? screenIsCustom : value === config.screenColor) && <div>Screen</div>}
                            </div>
                          )
                        )
                      })()}

                      {/* Premium Badge */}
                      {colorInfo.isPremium && (
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#e63946',
                            marginRight: 8,
                            fontFamily: 'Jost, sans-serif'
                          }}>
                          +${colorInfo.premiumPrice}
                        </span>
                      )}

                      <div style={{ fontSize: 20, fontWeight: 700, color: '#999', fontFamily: 'Jost, sans-serif' }}>{isOpen ? '−' : '+'}</div>
                    </div>

                    {/* Expandable content */}
                    {isOpen && (
                      <div style={{ padding: '0 12px 12px 12px', borderTop: '1px solid #e6e8ee' }}>
                        {/* Larger Swatch Image */}
                        {colorInfo.rectangleSwatchUrl && (
                          <div
                            style={{
                              width: '100%',
                              height: 120,
                              borderRadius: 8,
                              marginTop: 12,
                              marginBottom: 12,
                              backgroundImage: `url("${colorInfo.rectangleSwatchUrl}")`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              border: '1px solid #e0e0e0'
                            }}
                          />
                        )}
                        <p style={{ color: '#666', marginTop: 12, marginBottom: 16, fontFamily: 'Jost, sans-serif', lineHeight: 1.5 }}>
                          {colorInfo.description || 'No description available.'}
                        </p>

                        {/* Example Images - Three portrait placeholders */}
                        {SHOW_COLOR_GUIDE_PORTRAITS &&
                          expandedPhoto === null &&
                          (() => {
                            const portraits = colorGuidePortraits[colorInfo.name || name] || colorInfo.portraits || []
                            if (portraits.length === 0) return null
                            return (
                              <div
                                style={{
                                  display: 'flex',
                                  gap: '16px',
                                  marginTop: '16px',
                                  justifyContent: 'center'
                                }}>
                                {portraits.map((portrait, i) => (
                                  <div
                                    key={i}
                                    onClick={() => setExpandedPhoto(i)}
                                    style={{
                                      flex: 1,
                                      aspectRatio: '3/4',
                                      backgroundColor: '#f5f5f5',
                                      borderRadius: '8px',
                                      border: '1px solid #e0e0e0',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: '#999',
                                      fontSize: '12px',
                                      fontFamily: 'Jost, sans-serif',
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s',
                                      backgroundImage: portrait.imageUrl ? `url("${portrait.imageUrl}")` : 'none',
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center'
                                    }}
                                    onTouchStart={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                                    onTouchEnd={(e) => (e.currentTarget.style.transform = 'scale(1)')}>
                                    {!portrait.imageUrl && <span>{i + 1}</span>}
                                  </div>
                                ))}
                              </div>
                            )
                          })()}

                        {/* Expanded Photo View */}
                        {SHOW_COLOR_GUIDE_PORTRAITS && expandedPhoto !== null && (
                          <div
                            style={{
                              position: 'fixed',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: '#fff',
                              borderTopLeftRadius: '24px',
                              borderTopRightRadius: '24px',
                              zIndex: 1000,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              paddingTop: '80px',
                              paddingLeft: '20px',
                              paddingRight: '20px',
                              paddingBottom: '40px'
                            }}>
                            {/* Handle bar at top */}
                            <div
                              style={{
                                position: 'absolute',
                                top: '12px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '40px',
                                height: '4px',
                                backgroundColor: '#d1d5db',
                                borderRadius: '2px'
                              }}
                            />

                            {/* Back Button */}
                            <button
                              type="button"
                              onClick={() => setExpandedPhoto(null)}
                              style={{
                                position: 'absolute',
                                top: '20px',
                                left: '20px',
                                background: 'transparent',
                                border: 'none',
                                padding: '8px 0',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                color: '#666',
                                fontSize: 14,
                                fontWeight: 600,
                                fontFamily: 'Jost, sans-serif'
                              }}>
                              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                              </svg>
                              Back to Color Guide
                            </button>

                            {/* Expanded photo with color labels */}
                            <div style={{ width: '70%', maxWidth: '360px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              {/* Photo */}
                              {(() => {
                                const portraits = colorGuidePortraits[colorInfo.name || name] || colorInfo.portraits || []
                                const portrait = portraits[expandedPhoto] || {}
                                const len = portraits.length
                                return (
                                  <div
                                    style={{
                                      position: 'relative',
                                      width: '100%',
                                      aspectRatio: '3/4',
                                      maxHeight: '45vh',
                                      backgroundColor: '#f5f5f5',
                                      borderRadius: '12px',
                                      border: '2px solid #e0e0e0',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: '#999',
                                      fontSize: '24px',
                                      fontFamily: 'Jost, sans-serif',
                                      backgroundImage: portrait.imageUrl ? `url("${portrait.imageUrl}")` : 'none',
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center'
                                    }}>
                                    {!portrait.imageUrl && <span>{expandedPhoto + 1}</span>}
                                    {len > 1 && (
                                      <>
                                        <button
                                          type="button"
                                          aria-label="Previous photo"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            setExpandedPhoto((expandedPhoto - 1 + len) % len)
                                          }}
                                          style={{
                                            position: 'absolute',
                                            left: '8px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: '44px',
                                            height: '44px',
                                            borderRadius: '50%',
                                            background: 'rgba(255,255,255,0.9)',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 0,
                                            WebkitTapHighlightColor: 'transparent'
                                          }}>
                                          <svg viewBox="0 0 24 24" width="24" height="24" fill="#333">
                                            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                                          </svg>
                                        </button>
                                        <button
                                          type="button"
                                          aria-label="Next photo"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            setExpandedPhoto((expandedPhoto + 1) % len)
                                          }}
                                          style={{
                                            position: 'absolute',
                                            right: '8px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: '44px',
                                            height: '44px',
                                            borderRadius: '50%',
                                            background: 'rgba(255,255,255,0.9)',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 0,
                                            WebkitTapHighlightColor: 'transparent'
                                          }}>
                                          <svg viewBox="0 0 24 24" width="24" height="24" fill="#333">
                                            <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
                                          </svg>
                                        </button>
                                      </>
                                    )}
                                  </div>
                                )
                              })()}

                              {/* Color Labels */}
                              {(() => {
                                const portrait = (colorGuidePortraits[colorInfo.name || name] || colorInfo.portraits || [])[expandedPhoto] || {}
                                return (
                                  <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', fontFamily: 'Jost, sans-serif' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flex: 1 }}>
                                      <div style={{ fontSize: '11px', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Body Color
                                      </div>
                                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#333', textAlign: 'center' }}>
                                        {portrait.bodyColor || colorInfo.name || name}
                                      </div>
                                    </div>

                                    <div style={{ width: '1px', backgroundColor: '#e0e0e0' }}></div>

                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flex: 1 }}>
                                      <div style={{ fontSize: '11px', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Screen Color
                                      </div>
                                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#333', textAlign: 'center' }}>
                                        {portrait.screenColor || colorInfo.name || name}
                                      </div>
                                    </div>
                                  </div>
                                )
                              })()}

                              {/* Apply Colors Button */}
                              {(() => {
                                const portrait = (colorGuidePortraits[colorInfo.name || name] || colorInfo.portraits || [])[expandedPhoto] || {}
                                const bodyHex = Object.entries(colorOptions).find(([, n]) => n === portrait.bodyColor)?.[0] || value || null
                                const screenHex =
                                  Object.entries(colorOptions).find(([, n]) => n === portrait.screenColor)?.[0] ||
                                  Object.entries(screenColorOptions).find(([, n]) => n === portrait.screenColor)?.[0] ||
                                  (portrait.screenColor && screenColorOptions[portrait.screenColor] ? portrait.screenColor : null) ||
                                  value ||
                                  null
                                if (!onApplyColors) return null
                                return (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      onApplyColors(bodyHex, screenHex)
                                      setExpandedPhoto(null)
                                      if (onFooterToMiddle) onFooterToMiddle()
                                    }}
                                    style={{
                                      marginTop: '12px',
                                      padding: '14px 24px',
                                      background: '#6c8ce6',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      fontFamily: 'roc-grotesk-wide, sans-serif',
                                      fontWeight: 600,
                                      fontSize: '13px',
                                      letterSpacing: '0.05em',
                                      width: '100%'
                                    }}
                                    onMouseEnter={undefined}
                                    onMouseLeave={undefined}>
                                    Apply These Colors
                                  </button>
                                )
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            })()}
          </div>
        </div>

        {/* Pop Filter Tab */}
        <div className="pm-page" style={{ display: activeTab === 'popFilter' ? 'block' : 'none' }} aria-hidden={activeTab !== 'popFilter'}>
          <h3 style={{ marginTop: 0, fontFamily: "'roc-grotesk-wide', sans-serif", fontWeight: 600 }}>
            Pop Filter
          </h3>
          <div style={{ marginTop: 16 }}>
            {(() => {
              const popTopics = INFO_COPY.popFilter.map((t) => ({ ...t, label: t.title }))

              return popTopics.map((topic) => {
                const isOpen = openAccordion === `pop-${topic.id}`
                return (
                  <div key={topic.id} style={{ marginBottom: 12, border: '1px solid #e6e8ee', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                    <div
                      onClick={() => setOpenAccordion(isOpen ? null : `pop-${topic.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, cursor: 'pointer' }}>
                      <div style={{ flex: 1, fontWeight: 700, fontSize: 15, fontFamily: 'Jost, sans-serif' }}>{topic.label}</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#999', fontFamily: 'Jost, sans-serif' }}>{isOpen ? '−' : '+'}</div>
                    </div>
                    {isOpen && (
                      <div style={{ padding: '0 12px 12px 12px', borderTop: '1px solid #e6e8ee' }}>
                        {topic.id === 'whichOption' ? (
                          <>
                            <div style={{ marginTop: 12, marginBottom: 12 }}>
                              <p
                                style={{
                                  fontFamily: "'roc-grotesk-wide', sans-serif",
                                  fontWeight: 700,
                                  fontSize: '0.65em',
                                  letterSpacing: '0.08em',
                                  textTransform: 'uppercase',
                                  color: '#333',
                                  marginBottom: 4,
                                  marginTop: 0
                                }}>
                                None
                              </p>
                              <p style={{ color: '#666', marginTop: 0, marginBottom: 0, fontFamily: 'Jost, sans-serif', lineHeight: 1.6 }}>
                                Choose this if you prefer to use the mic open or already have a pop filter setup you like. You can always add one later.
                              </p>
                            </div>
                            <div style={{ marginBottom: 12 }}>
                              <p
                                style={{
                                  fontFamily: "'roc-grotesk-wide', sans-serif",
                                  fontWeight: 700,
                                  fontSize: '0.65em',
                                  letterSpacing: '0.08em',
                                  textTransform: 'uppercase',
                                  color: '#333',
                                  marginBottom: 4,
                                  marginTop: 0
                                }}>
                                Black
                              </p>
                              <p style={{ color: '#666', marginTop: 0, marginBottom: 0, fontFamily: 'Jost, sans-serif', lineHeight: 1.6 }}>
                                Our black pop filter uses the same magnetic snap-in design and acoustic system, with a lightweight build that keeps things
                                simple and easy to use. It's $79 in the builder and $99 on its own.
                              </p>
                            </div>
                            <div>
                              <p
                                style={{
                                  fontFamily: "'roc-grotesk-wide', sans-serif",
                                  fontWeight: 700,
                                  fontSize: '0.65em',
                                  letterSpacing: '0.08em',
                                  textTransform: 'uppercase',
                                  color: '#333',
                                  marginBottom: 4,
                                  marginTop: 0
                                }}>
                                Match Color
                              </p>
                              <p style={{ color: '#666', marginTop: 0, marginBottom: 8, fontFamily: 'Jost, sans-serif', lineHeight: 1.6 }}>
                                Our upgraded metal version, finished to match your microphone. The frame is made from metal, so it has a little more weight than
                                the black version. If you want it to match, add it now. Ordering one later requires a separate custom build.
                              </p>
                              <ul style={{ color: '#666', marginTop: 0, marginBottom: 8, fontFamily: 'Jost, sans-serif', lineHeight: 1.6, paddingLeft: 20 }}>
                                <li>$149 now</li>
                                <li>$199 later (Ohma Builder colors)</li>
                                <li>$399 later (HEX custom colors)</li>
                              </ul>
                              <p style={{ color: '#666', marginTop: 0, marginBottom: 0, fontFamily: 'Jost, sans-serif', lineHeight: 1.6 }}>
                                HEX custom colors are harder to recreate after the mic is already made. We can get very close, but an exact match is not
                                guaranteed.
                              </p>
                            </div>
                          </>
                        ) : (
                          topic.paragraphs.map((p, i) => (
                            <p key={i} style={{ color: '#666', marginTop: i === 0 ? 12 : 8, marginBottom: 0, fontFamily: 'Jost, sans-serif' }}>
                              {p}
                            </p>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            })()}
          </div>
        </div>

        {/* Special Orders Tab */}
        <div className="pm-page" style={{ display: activeTab === 'specialOrders' ? 'block' : 'none' }} aria-hidden={activeTab !== 'specialOrders'}>
          <h3 style={{ marginTop: 0, fontFamily: "'roc-grotesk-wide', sans-serif", fontWeight: 600 }}>Special Orders</h3>
          <div style={{ marginTop: 16 }}>
            {(() => {
              const specialPhotos = [
                {
                  src: 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/flecks-and-blems-motif-condenser.webp',
                  name: 'Flecks & Blems',
                  description:
                    'A one-of-a-kind condenser built around an intentional imperfection theme. Speckled cerakote texture with hand-selected blemish marks turned into a design element, making every unit genuinely unique.'
                },
                {
                  src: 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/ghost-galaxy-motif-ribbon.webp',
                  name: 'Ghost Galaxy',
                  description:
                    'A ribbon mic finished in deep space tones — layered translucent grays and cool silvers that shift under light. The motif screen adds a celestial texture that ties the whole build together.'
                },
                {
                  src: 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/price-ombre-mic-motif-condenser.webp',
                  name: 'Pride Ombré',
                  description:
                    'A gradient condenser celebrating the pride flag spectrum, blended across the body using a custom multi-stage cerakote process. Each section transitions cleanly from one color to the next.'
                },
                {
                  src: 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/trans-rights-motif-condenser.webp',
                  name: 'Trans Rights',
                  description:
                    'A statement build finished in the colors of the trans pride flag. Clean, deliberate, and made to be seen. A portion of proceeds from this build went directly to trans rights organizations.'
                },
                {
                  src: 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/tuxedo-cat-rigby-motif-condenser.webp',
                  name: 'Tuxedo Cat Rigby',
                  description:
                    "A monochrome masterpiece with main character energy and serious cattitude. Inspired by the real Rigby (Ohma's most judgmental studio assistant), this gradient mic fades from black to white, just like a perfectly dressed tuxedo cat on its way to sabotage your best take."
                },
                {
                  src: 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/verdigris-patina-motif-condenser.webp',
                  name: 'Verdigris Patina',
                  description:
                    'Inspired by aged copper and oxidized bronze, this condenser was finished using a layered patina technique that creates depth and variation across the surface. No two look exactly the same.'
                }
              ]
              const expandedItem = expandedSpecialPhoto !== null ? specialPhotos[expandedSpecialPhoto] : null
              return (
                <>
                  <p style={{ color: '#666', marginTop: 0, marginBottom: 16, fontFamily: 'Jost, sans-serif', lineHeight: 1.6, fontSize: 14 }}>
                    One of our favorite things to do at Ohma is help turn ambitious mic ideas into real builds. We've made all-brass patina mics, gradient color
                    microphones, microphones inspired by Kintsugi repair, watercolor-style finishes, and even microphones with wood screens. Special orders can
                    involve extra development, testing, or sourcing, but pushing the platform in new directions is part of the process.
                  </p>
                  <a
                    href="https://ohmaworld.com/collections/special-orders"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      marginBottom: 20,
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: 700,
                      fontSize: 14,
                      color: '#333',
                      textDecoration: 'underline'
                    }}>
                    Start a Special Order
                  </a>
                  {expandedSpecialPhoto === null && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginTop: '16px' }}>
                      {specialPhotos.map((photo, i) => (
                        <div
                          key={i}
                          onClick={() => setExpandedSpecialPhoto(i)}
                          style={{
                            aspectRatio: '3/4',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#999',
                            fontSize: '12px',
                            fontFamily: 'Jost, sans-serif',
                            cursor: 'pointer',
                            transition: 'transform 0.2s',
                            backgroundImage: photo.src ? `url("${photo.src}")` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }}
                          onTouchStart={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                          onTouchEnd={(e) => (e.currentTarget.style.transform = 'scale(1)')}>
                          {!photo.src && <span>{i + 1}</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Expanded photo overlay — matches color guide exactly */}
                  {expandedItem && (
                    <div
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: '#fff',
                        borderTopLeftRadius: '24px',
                        borderTopRightRadius: '24px',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        paddingTop: '80px',
                        paddingLeft: '20px',
                        paddingRight: '20px',
                        paddingBottom: '40px',
                        overflowY: 'auto'
                      }}>
                      {/* Handle bar */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '12px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '40px',
                          height: '4px',
                          backgroundColor: '#d1d5db',
                          borderRadius: '2px'
                        }}
                      />
                      {/* Back button */}
                      <button
                        type="button"
                        onClick={() => setExpandedSpecialPhoto(null)}
                        style={{
                          position: 'absolute',
                          top: '20px',
                          left: '20px',
                          background: 'transparent',
                          border: 'none',
                          padding: '8px 0',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          color: '#666',
                          fontSize: 14,
                          fontWeight: 600,
                          fontFamily: 'Jost, sans-serif'
                        }}>
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                        </svg>
                        Back to Special Orders
                      </button>
                      {/* Photo + name */}
                      <div style={{ width: '70%', maxWidth: '360px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div
                          style={{
                            width: '100%',
                            width: '100%',
                            aspectRatio: '3/4',
                            maxHeight: '55vh',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '12px',
                            border: '2px solid #e0e0e0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#999',
                            fontSize: '24px',
                            fontFamily: 'Jost, sans-serif',
                            backgroundImage: expandedItem.src ? `url("${expandedItem.src}")` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }}>
                          {!expandedItem.src && <span>{expandedSpecialPhoto + 1}</span>}
                        </div>
                        <div style={{ fontFamily: "'roc-grotesk-wide', sans-serif", fontWeight: 700, fontSize: 15, color: '#222', textAlign: 'center' }}>
                          {expandedItem.name || ''}
                        </div>
                        {expandedItem.description && (
                          <p style={{ color: '#666', fontSize: 13, lineHeight: 1.6, textAlign: 'left', margin: '8px 0 0', fontFamily: 'Jost, sans-serif' }}>
                            {expandedItem.description}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )
            })()}
          </div>
        </div>

        {/* Give Feedback Tab */}
        <div className="pm-page" style={{ display: activeTab === 'savedDesigns' ? 'block' : 'none' }} aria-hidden={activeTab !== 'savedDesigns'}>
          <SavedDesignsTab
            config={{}}
            onLoadDesign={onLoadDesign}
            onSaveDesign={onSaveDesign}
            onUpdateDesign={onUpdateDesign}
            currentDesignId={currentDesignId}
            currentDesignName={currentDesignName}
            hasUnsavedChanges={hasUnsavedChanges}
            shareScreenshotUrl={shareScreenshotUrl}
            onRegenerateScreenshot={onRegenerateScreenshot}
            onScrollReset={() => {
              if (contentRef.current) contentRef.current.scrollTop = 0
            }}
            onMigratedId={onMigratedId}
          />
        </div>

        <div className="pm-page" style={{ display: activeTab === 'feedback' ? 'block' : 'none' }} aria-hidden={activeTab !== 'feedback'}>
          <h3 style={{ marginTop: 0, fontFamily: "'roc-grotesk-wide', sans-serif", fontWeight: 600 }}>Give Feedback</h3>
          <div style={{ marginTop: 16, fontFamily: 'Jost, sans-serif' }}>
            <p style={{ color: '#666', marginTop: 8, marginBottom: 14, lineHeight: 1.6, fontFamily: 'Jost, sans-serif', fontSize: 14 }}>
              We're always refining the Ohma builder. If something feels confusing, unexpected, or worth improving, we want to hear it.
            </p>
            <p style={{ color: '#666', marginBottom: 20, lineHeight: 1.6, fontFamily: 'Jost, sans-serif', fontSize: 14 }}>
              We read every submission and use it to shape how the system evolves. This goes directly to our team in East Los Angeles. Updates are tracked in
              the changelog.
            </p>
            <a
              href="https://dl43o.share.hsforms.com/22arrf6arQW6Iad6E-jlUaQ"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                marginBottom: 20,
                fontFamily: 'Jost, sans-serif',
                fontWeight: 700,
                fontSize: 14,
                color: '#333',
                textDecoration: 'underline'
              }}>
              Share Feedback
            </a>
            <br />
            <a
              href="https://ohmaworld.notion.site/change-log?utm_source=ohma-world-website&utm_medium=feedback&utm_campaign=modal-2026"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#aaa', fontSize: '0.85em', textDecoration: 'underline' }}>
              Release Notes &rarr;
            </a>
          </div>
        </div>

        {/* Share Tab */}
        <div className="pm-page" style={{ display: activeTab === 'share' ? 'block' : 'none' }} aria-hidden={activeTab !== 'share'}>
          {/* Instagram Story Instructions View */}
          {showInstagramInstructions ? (
            <div style={{ fontFamily: 'Jost, sans-serif' }}>
              {/* Back Button */}
              <button
                type="button"
                onClick={() => setShowInstagramInstructions(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '8px 0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  color: '#666',
                  fontSize: 14,
                  fontWeight: 600,
                  marginBottom: 16,
                  fontFamily: 'Jost, sans-serif'
                }}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
                Back to sharing options
              </button>

              <h3 style={{ marginTop: 0, fontSize: 20, fontWeight: 600, marginBottom: 20, fontFamily: "'roc-grotesk-wide', sans-serif" }}>
                How to add link stickers:
              </h3>

              {/* Step 1 with visual */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, background: '#f5f5f5', borderRadius: 12, padding: 16 }}>
                  <div
                    style={{
                      minWidth: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: '#6186F7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                      fontWeight: 700,
                      color: 'white',
                      fontFamily: 'Jost, sans-serif'
                    }}>
                    1
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, fontFamily: 'Jost, sans-serif' }}>Tap the sticker icon</div>
                    <div style={{ fontSize: 14, color: '#666', fontFamily: 'Jost, sans-serif' }}>
                      After creating your story, tap the square smiley face sticker icon at the top
                    </div>
                  </div>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      background: 'white',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #e0e0e0'
                    }}>
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="#666">
                      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Step 2 with visual */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, background: '#f5f5f5', borderRadius: 12, padding: 16 }}>
                  <div
                    style={{
                      minWidth: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: '#6186F7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                      fontWeight: 700,
                      color: 'white',
                      fontFamily: 'Jost, sans-serif'
                    }}>
                    2
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, fontFamily: 'Jost, sans-serif' }}>Find and tap the LINK option</div>
                    <div style={{ fontSize: 14, color: '#666', fontFamily: 'Jost, sans-serif' }}>
                      Scroll through the stickers and select the 🔗 LINK sticker
                    </div>
                  </div>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      background: 'white',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #e0e0e0',
                      fontSize: 24
                    }}>
                    🔗
                  </div>
                </div>
              </div>

              {/* Step 3 with visual */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, background: '#f5f5f5', borderRadius: 12, padding: 16 }}>
                  <div
                    style={{
                      minWidth: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: '#6186F7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                      fontWeight: 700,
                      color: 'white',
                      fontFamily: 'Jost, sans-serif'
                    }}>
                    3
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, fontFamily: 'Jost, sans-serif' }}>Paste your link</div>
                    <div style={{ fontSize: 14, color: '#666', fontFamily: 'Jost, sans-serif' }}>Paste the link below and position it on your story</div>
                  </div>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      background: 'white',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #e0e0e0'
                    }}>
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="#666">
                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Copy Link Box */}
              <div style={{ background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6, fontFamily: 'Jost, sans-serif' }}>Your link</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div
                    style={{
                      flex: 1,
                      fontSize: 13,
                      color: '#333',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontFamily: 'Jost, sans-serif'
                    }}>
                    {effectiveShareUrl || 'Generating link...'}
                  </div>
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    disabled={!effectiveShareUrl}
                    style={{
                      background: linkCopied ? '#4caf50' : '#fff',
                      border: '1px solid #e0e0e0',
                      borderRadius: 6,
                      padding: '6px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: effectiveShareUrl ? 'pointer' : 'not-allowed',
                      color: linkCopied ? '#fff' : '#333',
                      fontFamily: 'Jost, sans-serif',
                      opacity: effectiveShareUrl ? 1 : 0.5
                    }}>
                    {linkCopied ? '✓' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Open Instagram Button */}
              <button
                type="button"
                onClick={handleOpenInstagram}
                disabled={!effectiveShareUrl}
                style={{
                  width: '100%',
                  background: '#6186F7',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  padding: '14px 24px',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: effectiveShareUrl ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  fontFamily: 'Jost, sans-serif',
                  opacity: effectiveShareUrl ? 1 : 0.5
                }}>
                Open Instagram
              </button>
            </div>
          ) : (
            /* Main Share Content - Now using ShareContent component */
            <ShareContent shareUrl={effectiveShareUrl} screenshotUrl={shareScreenshotUrl} config={config} onGenerateShareLink={onGenerateShareLink} />
          )}
        </div>
      </div>

      {/* Bottom gradient - only show when there's more content below */}
      {canScrollMore && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: '38px',
            height: '80px',
            background: 'linear-gradient(to top, #ffffff 0%, rgba(255, 255, 255, 0) 100%)',
            pointerEvents: 'none',
            zIndex: 10
          }}
        />
      )}
    </div>
  )
}
